import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Container,
    Typography,
    Button,
    AppBar,
    Toolbar,
    Drawer,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Grid,
    Card,
    CardContent,
    Avatar,
    Chip,
    useTheme,
    useMediaQuery,
    Fade,
    Slide,
    Grow,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    Skeleton,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    Menu as MenuIcon,
    Close as CloseIcon,
    Person as PersonIcon,
    Schedule as ScheduleIcon,
    Security as SecurityIcon,
    Support as SupportIcon,
    Star as StarIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    ArrowForward as ArrowForwardIcon,
    CheckCircle as CheckCircleIcon,
    ExpandMore as ExpandMoreIcon,
    Android as AndroidIcon,
    Apple as AppleIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import { useGetFeaturedDoctorsQuery } from '../../store/api/doctorApiSlice';
import { useGetProfessionsQuery } from '../../store/api/professionApiSlice';
import PrivacyAgreement from '../agreementPages/PrivacyAgreement';
import TermsOfUse from '../agreementPages/TermsOfUse';
import UserAgreement from '../agreementPages/UserAgreement';
import { formatBranchDisplay } from '../../utils/formatBranchDisplay';
import { getSpecialtyName, getTaxonomyEntityName } from '../../utils/taxonomyFields';
import { appColors } from "../../constants/theme";
import { getPublicFileUrl } from "../../utils/storedFile";

/** Anasayfa doktor kartı: profil fotoğrafı (yeşil nokta fotoğrafın sağ altında), isim/unvan, uzmanlık, cinsiyet, şehir. Tıklanınca uygulama indirme popup'ı açılır. */
const LandingDoctorCard = ({ doctor, theme, onCardClick }) => {
    const { t } = useTranslation();
    const photoUrl = getPublicFileUrl(doctor.profilePhotoName);
    const genderLabel = doctor.gender === 'Female' ? t('female') : doctor.gender === 'Male' ? t('male') : doctor.gender || '';

    return (
        <Card
            onClick={onCardClick}
            sx={{
                width: 380,
                minHeight: 160,
                maxHeight: 160,
                borderRadius: 2,

                overflow: 'hidden',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {

                },
            }}
        >
            <CardContent sx={{ p: 0, display: 'flex', flexDirection: 'row', alignItems: 'stretch' }}>
                <Box sx={{ flexShrink: 0, m: 2 }}>
                    <Box sx={{ position: 'relative', width: 120, height: 120 }}>
                        {photoUrl ? (
                            <Box
                                component="img"
                                src={photoUrl}
                                alt={doctor.name}
                                sx={{
                                    width: 120,
                                    height: 120,
                                    objectFit: 'cover',
                                    borderRadius: '12px',
                                    backgroundColor: theme.palette.grey[200],
                                    display: 'block',
                                }}
                            />
                        ) : (
                            <Avatar
                                sx={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: '12px',
                                    fontSize: '2rem',
                                    bgcolor: theme.palette.primary.main,
                                }}
                            >
                                {doctor.name.split(' ')[1]?.[0] || doctor.name[0] || '?'}
                            </Avatar>
                        )}
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: appColors.success,
                                border: '2px solid white',
                            }}
                        />
                    </Box>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, py: 2, pr: 2, pl: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: appColors.gray900, mb: 0.5 }}>
                        {doctor.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: appColors.gray500, mb: 1 }}>
                        {doctor.specialty}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        {genderLabel && (
                            <Typography variant="caption" sx={{ color: appColors.gray500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {doctor.gender === 'Female' ? '♀' : '♂'} {genderLabel}
                            </Typography>
                        )}
                        {doctor.city && (
                            <Typography variant="caption" sx={{ color: appColors.gray500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationIcon sx={{ fontSize: 14 }} />
                                {doctor.city}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const LandingPage = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [downloadOpen, setDownloadOpen] = useState(false);
    const [agreementOpen, setAgreementOpen] = useState(false);
    const [agreementType, setAgreementType] = useState(null); // 'privacy' | 'terms' | 'user'
    const { data: doctorsResponse, isLoading: doctorsLoading } = useGetFeaturedDoctorsQuery(6, { refetchOnMountOrArgChange: false });
    const { data: categoriesResponse, isLoading: categoriesLoading } = useGetProfessionsQuery(undefined, { refetchOnMountOrArgChange: false });

    const handleLogin = () => {
        navigate('/login');
    };

    const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || 'https://play.google.com/store/apps';
    const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL || 'https://apps.apple.com/';

    const handleDownloadApp = () => {
        window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
    };

    const openAgreement = (type) => {
        setAgreementType(type);
        setAgreementOpen(true);
    };

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
        // section state removed; only smooth scroll and close drawer
        setDrawerOpen(false);
    };

    const features = [
        {
            icon: <ScheduleIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
            title: t('easy_appointment'),
            description: t('easy_appointment_description')
        },
        {
            icon: <SecurityIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
            title: t('secure_communication'),
            description: t('secure_communication_description')
        },
        {
            icon: <SupportIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
            title: t('support_24_7'),
            description: t('support_24_7_description')
        }
    ];

    // Map recommended doctors from /api/doctor/featured if available; otherwise graceful fallback to empty array
    const doctors = (Array.isArray(doctorsResponse?.data) ? doctorsResponse?.data : (Array.isArray(doctorsResponse?.items) ? doctorsResponse?.items : (Array.isArray(doctorsResponse) ? doctorsResponse : [])))
        .slice(0, 6)
        .map((d) => {
            const first = d?.firstName || d?.name || d?.doctorName || 'Uzman';
            const last = d?.lastName || '';
            const title = d?.title?.titleName || '';
            const displayName = `${title ? `${title} ` : ''}${[first, last].join(' ').trim()}`.trim();
            return {
                name: displayName,
                specialty: formatBranchDisplay(getSpecialtyName(d)) || 'Uzman',
                profilePhotoName: d?.profilePhotoName || null,
                city: d?.city || null,
                gender: d?.gender || null,
            };
        });

    // Map categories for Hizmetlerimiz
    const categories = (Array.isArray(categoriesResponse?.data) ? categoriesResponse?.data : (Array.isArray(categoriesResponse?.items) ? categoriesResponse?.items : (Array.isArray(categoriesResponse) ? categoriesResponse : [])))
        .map((c) => ({ id: c.id, specialtyCategoryName: getTaxonomyEntityName(c) }));

    const stats = [
        { number: '50,000+', label: t('happy_patient') },
        { number: '500+', label: t('expert_doctor') },
        { number: '100,000+', label: t('successful_meeting') },
        { number: '99.9%', label: t('satisfaction_rate') }
    ];

    return (
        <Box sx={{ minHeight: '100vh' }}>
            {/* AppBar */}
            <AppBar
                position="fixed"
                sx={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',

                    zIndex: 1000
                }}
            >
                <Toolbar>
                    <Typography
                        variant="h6"
                        sx={{
                            flexGrow: 1,
                            fontWeight: 700,
                            color: theme.palette.primary.main
                        }}
                    >
                        Medilya
                    </Typography>

                    {!isMobile && (
                        <Box sx={{ display: 'flex', gap: 2, mr: 2 }}>
                            <Button
                                color="inherit"
                                onClick={() => scrollToSection('home')}
                                sx={{ color: appColors.gray900, fontWeight: 500 }}
                            >
                                Ana Sayfa
                            </Button>
                            <Button
                                color="inherit"
                                onClick={() => scrollToSection('about')}
                                sx={{ color: appColors.gray900, fontWeight: 500 }}
                            >
                                Hakkımızda
                            </Button>
                            <Button
                                color="inherit"
                                onClick={() => scrollToSection('doctors')}
                                sx={{ color: appColors.gray900, fontWeight: 500 }}
                            >
                                Uzmanlarımız
                            </Button>
                            <Button
                                color="inherit"
                                onClick={() => scrollToSection('contact')}
                                sx={{ color: appColors.gray900, fontWeight: 500 }}
                            >
                                İletişim
                            </Button>
                            <Typography
                                component={Link}
                                to="/gizlilik-politikasi"
                                variant="body2"
                                sx={{ color: appColors.gray900, fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, alignSelf: 'center' }}
                            >
                                Gizlilik Politikası
                            </Typography>
                        </Box>
                    )}

                    <Button
                        variant="contained"
                        onClick={handleLogin}
                        sx={{
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            borderRadius: '25px',
                            px: 3,
                            py: 1,
                            fontWeight: 600,
                            textTransform: 'none',

                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,

                                transform: 'translateY(-1px)',
                            },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        Uzman Girişi
                    </Button>

                    {isMobile && (
                        <IconButton
                            color="inherit"
                            onClick={() => setDrawerOpen(true)}
                            sx={{ ml: 1, color: appColors.gray900 }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                </Toolbar>
            </AppBar>

            {/* Mobile Drawer */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                    sx: {
                        width: 250,
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                    }
                }}
            >
                <Box sx={{ p: 2 }}>
                    <IconButton onClick={() => setDrawerOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <List>
                    {[t('home_page'), t('about_us'), t('our_doctors'), t('contact')].map((text) => (
                        <ListItem
                            button
                            key={text}
                            onClick={() => scrollToSection(text.toLowerCase().replace(/ /g, ''))}
                        >
                            <ListItemText primary={text} />
                        </ListItem>
                    ))}
                    <ListItem button component={Link} to="/gizlilik-politikasi" onClick={() => setDrawerOpen(false)}>
                        <ListItemText primary="Gizlilik Politikası" />
                    </ListItem>
                </List>
            </Drawer>

            {/* Hero Section */}
            <Box
                id="home"
                sx={{
                    minHeight: '100vh',
                    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: 'radial-gradient(rgba(255,255,255,0.12) 1px, transparent 1px)',
                        backgroundSize: '28px 28px',
                        backgroundPosition: '0 0',
                        pointerEvents: 'none',
                    }
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Fade in timeout={1000}>
                                <Box>
                                    <Typography
                                        variant="h2"
                                        sx={{
                                            fontWeight: 700,
                                            color: 'white',
                                            mb: 2,
                                            fontSize: { xs: '2.5rem', md: '3.5rem' }
                                        }}
                                    >
                                        Sağlığınız İçin
                                        <br />
                                        <span style={{ color: 'rgba(255,255,255,0.92)' }}>Güvenli Dijital Klinik</span>
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            mb: 2,
                                            lineHeight: 1.6
                                        }}
                                    >
                                        Medilya, çevrimiçi danışan danışmanlığı ve randevu yönetimi sunan sağlık platformudur.
                                        Uygulama amacımız; hastaları uzmanlarla buluşturarak kaliteli sağlık hizmetlerini herkes için erişilebilir kılmaktır.
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: 'rgba(255, 255, 255, 0.85)',
                                            mb: 2,
                                            lineHeight: 1.6
                                        }}
                                    >
                                        Türkiye'nin önde gelen uzmanlarıyla saniyeler içinde görüşün.
                                        Randevu alın, güvenle konuşun, kişisel planınızı oluşturun.
                                    </Typography>
                                    <Box component="span" sx={{ display: 'block', mb: 4 }}>
                                        <Typography component={Link} to="/gizlilik-politikasi" sx={{ color: 'rgba(255,255,255,0.9)', textDecoration: 'underline', fontWeight: 500, fontSize: '0.875rem' }}>Gizlilik Politikası</Typography>
                                        <Typography component="span" sx={{ color: 'rgba(255,255,255,0.75)', mx: 0.5 }}> · </Typography>
                                        <Typography component={Link} to="/kullanim-kosullari" sx={{ color: 'rgba(255,255,255,0.9)', textDecoration: 'underline', fontWeight: 500, fontSize: '0.875rem' }}>Kullanım Koşulları</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => setDownloadOpen(true)}
                                            sx={{
                                                background: 'white',
                                                color: theme.palette.primary.main,
                                                borderRadius: '30px',
                                                px: 4,
                                                py: 1.5,
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                fontSize: '1.1rem',

                                                '&:hover': {
                                                    background: appColors.bgMuted,
                                                    transform: 'translateY(-2px)',

                                                },
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            Uygulamayı İndir
                                            <ArrowForwardIcon sx={{ ml: 1 }} />
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="large"
                                            onClick={() => scrollToSection('doctors')}
                                            sx={{
                                                borderColor: 'white',
                                                color: 'white',
                                                borderRadius: '30px',
                                                px: 4,
                                                py: 1.5,
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                fontSize: '1.1rem',
                                                '&:hover': {
                                                    borderColor: 'white',
                                                    background: 'rgba(255, 255, 255, 0.1)',
                                                }
                                            }}
                                        >
                                            Uzmanları Gör
                                        </Button>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: { xs: 0, md: 1 } }}>
                                            <AndroidIcon sx={{ color: 'white', opacity: 0.9 }} />
                                            <AppleIcon sx={{ color: 'white', opacity: 0.9 }} />
                                        </Stack>
                                    </Box>
                                </Box>
                            </Fade>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Slide direction="left" in timeout={1000}>
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        position: 'relative'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 400,
                                            height: 400,
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            borderRadius: '20px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto',
                                            backdropFilter: 'blur(10px)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            position: 'relative',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: -20,
                                                left: -20,
                                                right: -20,
                                                bottom: -20,
                                                background: 'rgba(255, 255, 255, 0.05)',
                                                borderRadius: '20px',
                                                zIndex: -1,
                                            }
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src="/home-banner.png"
                                            alt="Medilya uygulaması görseli"
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                width: { xs: 280, md: 460 },
                                                height: 'auto',
                                                objectFit: 'contain',
                                                zIndex: 1
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Slide>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Download Dialog */}
            <Dialog open={downloadOpen} onClose={() => setDownloadOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700, textAlign: 'center', px: 4, pt: 2, pb: 1 }}>Uygulamayı İndir</DialogTitle>
                <DialogContent sx={{ px: 4, pt: 2, pb: 0 }}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                            <Button
                                fullWidth
                                component="a"
                                href={PLAY_STORE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                startIcon={<AndroidIcon />}
                                sx={{
                                    height: 48,
                                    borderRadius: 2,
                                    background: 'white',
                                    color: theme.palette.primary.main,
                                    fontWeight: 700,
                                    '&:hover': { background: appColors.bgMuted }
                                }}
                            >
                                Google Play (Android)
                            </Button>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Button
                                fullWidth
                                component="a"
                                href={APP_STORE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                startIcon={<AppleIcon />}
                                sx={{
                                    height: 48,
                                    borderRadius: 2,
                                    background: 'white',
                                    color: theme.palette.primary.main,
                                    fontWeight: 700,
                                    '&:hover': { background: appColors.bgMuted }
                                }}
                            >
                                App Store (iOS)
                            </Button>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Button
                                fullWidth
                                onClick={handleDownloadApp}
                                startIcon={<DownloadIcon />}
                                sx={{
                                    height: 48,
                                    borderRadius: 2,
                                    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                                    color: theme.palette.primary.contrastText,
                                    fontWeight: 700,

                                    '&:hover': { background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)` }
                                }}
                            >
                                APK Olarak İndir
                            </Button>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
                    <Button onClick={() => setDownloadOpen(false)} sx={{ textTransform: 'none' }}>Kapat</Button>
                </DialogActions>
            </Dialog>

            {/* Agreement Dialog (fallback; ana linkler tam sayfa /gizlilik-politikasi vb.) */}
            <Dialog open={agreementOpen} onClose={() => setAgreementOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 2 } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>{agreementType === 'privacy' ? t('privacy_agreement') : agreementType === 'terms' ? t('terms_of_use') : t('membership_agreement')}</DialogTitle>
                <DialogContent dividers sx={{ maxHeight: '70vh' }}>
                    {agreementType === 'privacy' && <PrivacyAgreement />}
                    {agreementType === 'terms' && <TermsOfUse />}
                    {agreementType === 'user' && <UserAgreement />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAgreementOpen(false)}>Kapat</Button>
                </DialogActions>
            </Dialog>



            {/* Services (Hizmetlerimiz) - Specialty Categories */}
            <Box sx={{ py: 8, background: 'white' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, color: appColors.gray900, mb: 1 }}>Hizmetlerimiz</Typography>
                        <Typography variant="h6" sx={{ color: appColors.gray500 }}>Uzmanlık kategorilerinde hızlı erişim</Typography>
                    </Box>
                    {categoriesLoading ? (
                        <Grid container spacing={2} justifyContent="center">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <Grid size={{ xs: 6, sm: 4, md: 3 }} key={i}>
                                    <Card sx={{ borderRadius: 3 }}>
                                        <CardContent>
                                            <Skeleton variant="rounded" height={28} sx={{ borderRadius: 2 }} />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Grid container spacing={2} justifyContent="center">
                            {(categories?.length ? categories : []).slice(0, 20).map((cat, idx) => {
                                const colorPairs = [
                                    [appColors.infoBg, appColors.infoBg],
                                    [appColors.successBg, appColors.successBg],
                                    [appColors.warningBg, appColors.warningBg],
                                    [appColors.purpleBg, appColors.purpleBg],
                                    [appColors.infoBg, appColors.infoBg],
                                    [appColors.successBg, appColors.successBg],
                                ];
                                const pair = colorPairs[idx % colorPairs.length];
                                return (
                                    <Grid size={{ xs: 6, sm: 4, md: 3 }} key={cat.id || idx}>
                                        <Card
                                            onClick={() => setDownloadOpen(true)}
                                            sx={{
                                                borderRadius: 3,
                                                background: `linear-gradient(180deg, ${pair[0]} 0%, ${pair[1]} 100%)`,

                                                cursor: 'pointer',
                                                transition: 'transform .2s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-5px)',

                                                }
                                            }}
                                        >
                                            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 700, color: appColors.infoDark }}>{cat.specialtyCategoryName}</Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Container>
            </Box>

            {/* Stats Section */}
            <Box sx={{ py: 6, background: 'white' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4}>
                        {stats.map((stat, index) => (
                            <Grid size={{ xs: 6, md: 3 }} key={index}>
                                <Grow in timeout={500 + index * 200}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography
                                            variant="h3"
                                            sx={{
                                                fontWeight: 700,
                                                color: theme.palette.primary.main,
                                                mb: 1
                                            }}
                                        >
                                            {stat.number}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: appColors.gray500,
                                                fontWeight: 500
                                            }}
                                        >
                                            {stat.label}
                                        </Typography>
                                    </Box>
                                </Grow>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Features Section */}
            <Box sx={{ py: 8, background: appColors.bgMuted }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700,
                                color: appColors.gray900,
                                mb: 2
                            }}
                        >
                            Öne Çıkan Özellikler
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: appColors.gray500,
                                maxWidth: 600,
                                margin: '0 auto'
                            }}
                        >
                            Akıcı, güvenli ve hızlı bir deneyim için tasarlandı
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {features.map((feature, index) => (
                            <Grid size={{ xs: 12, md: 4 }} key={index}>
                                <Grow in timeout={500 + index * 200}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            borderRadius: '20px',
                                            background: `linear-gradient(180deg, ${appColors.white} 0%, ${appColors.infoBg} 100%)`,

                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-10px)',

                                            }
                                        }}
                                    >
                                        <CardContent sx={{ p: 4, textAlign: 'center' }}>
                                            <Box sx={{ mb: 3 }}>
                                                {feature.icon}
                                            </Box>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: appColors.gray900,
                                                    mb: 2
                                                }}
                                            >
                                                {feature.title}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color: appColors.gray500,
                                                    lineHeight: 1.6
                                                }}
                                            >
                                                {feature.description}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grow>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>



            {/* How it works */}
            <Box sx={{ py: 8, background: 'white' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 5 }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, color: appColors.landingDark, mb: 1 }}>Nasıl Çalışır?</Typography>
                        <Typography variant="h6" sx={{ color: appColors.gray500 }}>Dakikalar içinde sağlık uzmanına ulaşın</Typography>
                    </Box>
                    <Grid container spacing={3}>
                        {[
                            { n: '01', t: t('download_app'), d: t('download_app_description') },
                            { n: '02', t: t('choose_specialist'), d: t('choose_specialist_description') },
                            { n: '03', t: t('secure_meeting'), d: t('secure_meeting_description') },
                            { n: '04', t: t('plan_and_track'), d: t('plan_and_track_description') }
                        ].map((s, i) => (
                            <Grid size={{ xs: 12, md: 3 }} key={i}>
                                <Card sx={{ height: '100%', borderRadius: 3, p: 1 }}>
                                    <CardContent>
                                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                                            <Box sx={{ width: 38, height: 38, borderRadius: '50%', background: theme.palette.primary.main, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{s.n}</Box>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: appColors.gray900 }}>{s.t}</Typography>
                                        </Stack>
                                        <Typography variant="body2" sx={{ color: appColors.gray500, lineHeight: 1.7 }}>{s.d}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <Button variant="contained" onClick={() => setDownloadOpen(true)} sx={{ borderRadius: '24px' }}>Hemen Başla</Button>
                    </Box>
                </Container>
            </Box>

            {/* Doctors Section */}
            <Box id="doctors" sx={{ py: 8, background: 'white' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700,
                                color: appColors.gray900,
                                mb: 2
                            }}
                        >
                            Uzmanlarımız
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: appColors.gray500,
                                maxWidth: 600,
                                margin: '0 auto'
                            }}
                        >
                            Alanında uzmanlarımızla tanışın
                        </Typography>
                    </Box>

                    {doctorsLoading ? (
                        <Grid container spacing={4}>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <Grid size={{ xs: 12, md: 4 }} key={i}>
                                    <Card sx={{ borderRadius: '20px' }}>
                                        <CardContent sx={{ p: 4 }}>
                                            <Skeleton variant="circular" width={100} height={100} sx={{ mx: 'auto', mb: 3 }} />
                                            <Skeleton variant="text" height={28} sx={{ mx: 'auto', width: '60%' }} />
                                            <Skeleton variant="text" height={20} sx={{ mx: 'auto', width: '40%' }} />
                                            <Skeleton variant="rounded" height={24} sx={{ mx: 'auto', mt: 2, width: '50%', borderRadius: 2 }} />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Grid container spacing={4}>
                            {(doctors?.length ? doctors : []).map((doctor, index) => (
                                <Grid size={{ xs: 12, md: 4 }} key={index}>
                                    <Grow in timeout={500 + index * 200}>
                                        <Box component="div" sx={{ display: 'block' }}>
<LandingDoctorCard
                                            doctor={doctor}
                                            theme={theme}
                                            onCardClick={() => setDownloadOpen(true)}
                                        />
                                        </Box>
                                    </Grow>
                                </Grid>
                            ))}
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ textAlign: 'center', mt: 1 }}>
                                    <Button variant="text" size="medium" onClick={() => setDownloadOpen(true)} sx={{
                                        color: theme.palette.primary.main, textTransform: 'none',
                                        '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
                                    }}>
                                        Tümünü Gör
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </Container>
            </Box>

            {/* About Section */}
            <Box id="about" sx={{ py: 8, background: appColors.bgMuted }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: 700,
                                    color: appColors.gray900,
                                    mb: 3
                                }}
                            >
                                Hakkımızda
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: appColors.gray500,
                                    lineHeight: 1.8,
                                    mb: 3
                                }}
                            >
                                Medilya, Türkiye'nin önde gelen çevrim içi sağlık platformudur.
                                Uygulama amacımız; hastaları uzmanlarla buluşturarak
                                kaliteli sağlık hizmetlerini herkes için erişilebilir kılmaktır.
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: appColors.gray500,
                                    lineHeight: 1.8,
                                    mb: 4
                                }}
                            >
                                Modern teknoloji ve güvenilir altyapımızla,
                                binlerce hastaya hizmet veriyor ve sürekli olarak
                                hizmet kalitemizi artırıyoruz.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircleIcon sx={{ color: appColors.success, fontSize: 20 }} />
                                    <Typography variant="body2" sx={{ color: appColors.gray500 }}>
                                        Güvenli Platform
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircleIcon sx={{ color: appColors.success, fontSize: 20 }} />
                                    <Typography variant="body2" sx={{ color: appColors.gray500 }}>
                                        Uzmanlar
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircleIcon sx={{ color: appColors.success, fontSize: 20 }} />
                                    <Typography variant="body2" sx={{ color: appColors.gray500 }}>
                                        7/24 Destek
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box
                                sx={{
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                    borderRadius: '20px',
                                    p: 4,
                                    color: 'white',
                                    textAlign: 'center'
                                }}
                            >
                                <PersonIcon sx={{ fontSize: 80, mb: 2 }} />
                                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                                    Misyonumuz
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                    Herkesin kaliteli sağlık hizmetlerine kolayca erişebilmesi için
                                    teknoloji ve tıbbı birleştiriyoruz.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Testimonials */}
            <Box sx={{ py: 8, background: 'white' }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" sx={{ textAlign: 'center', fontWeight: 700, mb: 4, color: appColors.landingDark }}>
                        Kullanıcılarımız Ne Diyor?
                    </Typography>
                    <Grid container spacing={3}>
                        {["Hızlıca randevu alabildim, uzmanım çok ilgiliydi.", "Güvenli görüşme ve pratik kullanım, kesinlikle tavsiye ederim.", "Uygulama sayesinde tedavi sürecim çok daha düzenli ilerledi."].map((quote, idx) => (
                            <Grid size={{ xs: 12, md: 4 }} key={idx}>
                                <Card sx={{ height: '100%', borderRadius: 3 }}>
                                    <CardContent>
                                        <Typography variant="body1" sx={{ color: appColors.gray500, lineHeight: 1.7 }}>
                                            “{quote}”
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* FAQ */}
            <Box sx={{ py: 8, background: appColors.bgMuted }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" sx={{ textAlign: 'center', fontWeight: 700, mb: 4, color: appColors.landingDark }}>
                        Sıkça Sorulan Sorular
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 8 }} sx={{ mx: 'auto' }}>
                            {[
                                { q: 'Ücretli mi?', a: 'Uygulamayı indirmek ücretsizdir. Görüşme ücretleri uzmanlara göre değişebilir.' },
                                { q: 'Görüşmeler güvenli mi?', a: 'Tüm görüşmeler uçtan uca şifreleme ile korunur ve KVKK uyumludur.' },
                                { q: 'Nasıl randevu alırım?', a: 'Uzman listesinden seçip size uygun zamana tek tıkla randevu alabilirsiniz.' },
                                { q: 'Kurumsal çözümler var mı?', a: 'Evet, kurumlara özel entegrasyon ve raporlama seçeneklerimiz mevcut.' }
                            ].map((item, i) => (
                                <Accordion key={i} sx={{ background: 'white', borderRadius: 2, mb: 1 }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography sx={{ fontWeight: 600 }}>{item.q}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography sx={{ color: appColors.gray500 }}>{item.a}</Typography>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* CTA Banner */}
            <Box sx={{ py: 8, background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)` }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid size={{ xs: 12, md: 7 }}>
                            <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 2 }}>
                                Medilya ile daha iyi bir sağlık deneyimi
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mb: 3 }}>
                                Uygulamayı indir, dakikalar içinde ilk görüşmeni yap.
                            </Typography>
                            <Stack direction="row" spacing={2}>
                                <Button variant="contained" onClick={handleDownloadApp} sx={{ background: 'white', color: theme.palette.primary.main, borderRadius: '24px', px: 3 }}>
                                    Android <AndroidIcon sx={{ ml: 1 }} />
                                </Button>
                                <Button variant="outlined" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.8)', borderRadius: '24px', px: 3 }}>
                                    iOS <AppleIcon sx={{ ml: 1 }} />
                                </Button>
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Box component="img" src="/home-banner.png" alt="Medilya" sx={{ width: { xs: '80%', md: '80%' }, filter: 'drop-shadow(0 18px 40px rgba(0,0,0,0.3))' }} />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Contact Section */}
            <Box id="contact" sx={{ py: 10, background: `linear-gradient(180deg, ${appColors.gray50} 0%, ${appColors.white} 50%)` }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 7 }}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700,
                                color: theme.palette.primary.main,
                                mb: 1,
                                letterSpacing: '-0.02em'
                            }}
                        >
                            İletişim
                        </Typography>
                        <Box sx={{ width: 64, height: 4, background: theme.palette.primary.main, borderRadius: 2, mx: 'auto', mb: 2 }} />
                        <Typography
                            variant="h6"
                            sx={{
                                color: appColors.landingSlate,
                                maxWidth: 480,
                                margin: '0 auto',
                                fontWeight: 400,
                                lineHeight: 1.6
                            }}
                        >
                            Sorularınız için bizimle iletişime geçin
                        </Typography>
                    </Box>

                    <Grid container spacing={3} justifyContent="center" alignItems="stretch">
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Card
                                elevation={0}
                                sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'rgba(0,0,0,0.06)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {

                                        borderColor: 'rgba(32,139,140,0.2)',
                                        transform: 'translateY(-4px)'
                                    }
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                                    <Box sx={{ width: 56, height: 56, borderRadius: 2, background: 'rgba(32,139,140,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                                        <PhoneIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: appColors.landingDarker }}>
                                        Telefon
                                    </Typography>
                                    <Typography
                                        component="a"
                                        href="tel:+905552579205"
                                        variant="body1"
                                        sx={{ color: appColors.landingSlate, textDecoration: 'none', '&:hover': { color: theme.palette.primary.main } }}
                                    >
                                        +90 555 257 92 05
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Card
                                elevation={0}
                                sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'rgba(0,0,0,0.06)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {

                                        borderColor: 'rgba(32,139,140,0.2)',
                                        transform: 'translateY(-4px)'
                                    }
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                                    <Box sx={{ width: 56, height: 56, borderRadius: 2, background: 'rgba(32,139,140,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                                        <EmailIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: appColors.landingDarker }}>
                                        E-posta
                                    </Typography>
                                    <Typography
                                        component="a"
                                        href="mailto:info@zenambilisim.com.tr"
                                        variant="body1"
                                        sx={{ color: appColors.landingSlate, textDecoration: 'none', wordBreak: 'break-all', '&:hover': { color: theme.palette.primary.main } }}
                                    >
                                        info@zenambilisim.com.tr
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Card
                                elevation={0}
                                sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'rgba(0,0,0,0.06)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {

                                        borderColor: 'rgba(32,139,140,0.2)',
                                        transform: 'translateY(-4px)'
                                    }
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                                    <Box sx={{ width: 56, height: 56, borderRadius: 2, background: 'rgba(32,139,140,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                                        <LocationIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: appColors.landingDarker }}>
                                        Adres
                                    </Typography>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="body1" component="div" sx={{ color: appColors.landingSlate, lineHeight: 1.7, fontSize: '0.95rem', overflowWrap: 'break-word' }}>
                                            OSTİM OSB Mahallesi, Cevat Dündar Caddesi, No:1/1/49, 06374
                                        </Typography>
                                        <Typography variant="body1" component="div" sx={{ color: appColors.landingSlate, lineHeight: 1.7, fontSize: '0.95rem' }}>
                                            OSTİM TEKNOPARK Yenimahalle/Ankara
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Footer */}
            <Box sx={{ py: 8, background: theme.palette.primary.main, color: 'white' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, letterSpacing: '-0.01em' }}>Medilya</Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mb: 3, lineHeight: 1.6 }}>
                                Sağlık profesyonelleri ile güvenli ve hızlı şekilde buluşun.
                            </Typography>
                            <Stack spacing={2}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <PhoneIcon sx={{ fontSize: 18, color: 'white' }} />
                                    </Box>
                                    <Typography component="a" href="tel:+905552579205" variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', textDecoration: 'none' }}>
                                        +90 555 257 92 05
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <EmailIcon sx={{ fontSize: 18, color: 'white' }} />
                                    </Box>
                                    <Typography component="a" href="mailto:info@zenambilisim.com.tr" variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', textDecoration: 'none', wordBreak: 'break-all' }}>
                                        info@zenambilisim.com.tr
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.25 }}>
                                        <LocationIcon sx={{ fontSize: 18, color: 'white' }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" component="span" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                                            OSTİM OSB Mahallesi, Cevat Dündar Caddesi, No:1/1/49, 06374
                                        </Typography>
                                        <Typography variant="body2" component="span" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6, display: 'block' }}>
                                            OSTİM TEKNOPARK Yenimahalle/Ankara
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 6, md: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Kurumsal</Typography>
                            <Stack spacing={1}>
                                <Typography component="a" href="#about" variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Hakkımızda</Typography>
                                <Typography component="a" href="#" variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Kariyer</Typography>
                                <Typography component="a" href="#" variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Basın</Typography>
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Destek</Typography>
                            <Stack spacing={1}>
                                <Typography component={Link} to="/sozlesmeler" variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, fontWeight: 600 }}>Sözleşmeler ve bilgilendirme</Typography>
                                <Typography component={Link} to="/uyelik-ve-hizmet-sozlesmesi" variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Üyelik ve Hizmet Sözleşmesi</Typography>
                                <Typography component={Link} to="/kisisel-pazarlama-acik-riza-metni" variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Kişisel Pazarlama Açık Rıza Metni</Typography>
                                <Typography component={Link} to="/uzaktan-saglik-bilgilendirme" variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Uzaktan Sağlık Hizmeti Bilgilendirme</Typography>
                                <Typography component={Link} to="/uzaktan-saglik-mesafeli-satis-sozlesmesi" variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Uzaktan Sağlık Mesafeli Satış Sözleşmesi</Typography>
                                <Typography component={Link} to="/kullanicilar-aydinlatma-metni" variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Üyelik ve Hizmet Aydınlatma Metni</Typography>
                                <Typography component={Link} to="/kvkk-politikasi" variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Kişisel Verilerin Korunması ve İşlenmesi Politikası</Typography>
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Uygulama</Typography>
                            <Stack spacing={1}>
                                <Button variant="contained" size="small" onClick={() => setDownloadOpen(true)} sx={{ alignSelf: 'flex-start', borderRadius: '20px', background: 'white', color: theme.palette.primary.main, '&:hover': { background: appColors.bgMuted } }}>Uygulamayı İndir</Button>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>KVKK ve güvenlik standartlarına uyumludur.</Typography>
                            </Stack>
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.08)' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>© 2026 Medilya. Tüm hakları saklıdır.</Typography>
                        <Stack direction="row" spacing={2}>
                            <Typography component={Link} to="/kullanim-kosullari" variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Kullanım Koşulları</Typography>
                            <Typography component={Link} to="/gizlilik-politikasi" variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Gizlilik Politikası</Typography>
                        </Stack>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default LandingPage; 